import { FC, useMemo } from 'react';
import { Panel } from '@hologram-dimension/panel';
import { PanelNotification } from '@hologram-dimension/panel-notification';
import useSimDetails from 'api/sims/useSimDetails';
import SimStatusChip from 'components/SimStatusChip/SimStatusChip';
import useSimStatuses from 'api/sims/useSimStatuses';
import useSimDeviceDetails from 'api/sims/useSimDeviceDetails';
import useUserInfo from 'hooks/User/useUserInfo';
import useOpenSessions from 'api/legacy/session/useOpenSessions';
import useStatusMap from 'utils/hooks/useStatusMap';
import clsx from 'clsx';
import { Icon, IconProps } from '@hologram-dimension/icon';
import { BannerNotification } from '@hologram-dimension/banner-notification';
import { Loader } from '@hologram-dimension/loader';
import ClickToCopy from 'components/ClickToCopy/ClickToCopy';
import styles from './ConnectivityStatusPanel.module.css';

interface ConnectivityStatusPanelProps {
  hologramSimId?: HologramSimId;
  // JUST FOR TESTING
  networkStatus?: string;
  data?: boolean;
  dataThisBillingPeriod?: boolean;
  zeroByteSessions?: boolean;
}

interface ConnectivityDiagramProps {
  bars: {
    bar1: string;
    bar2: string;
    bar3: string;
  };
  entities: {
    device: {
      style: string;
      icon: IconProps['name'];
    };
    sim: {
      style: string;
      icon: IconProps['name'];
    };
    core: {
      style: string;
      icon: IconProps['name'];
    };
    internet: {
      style: string;
      icon: IconProps['name'];
    };
  };
  statuses: {
    deviceToSim: {
      style: string;
      icon: IconProps['name'];
      label: string;
      text: string;
      size: 'small' | 'medium';
    } | null;
    simToCore: {
      style: string;
      icon: IconProps['name'];
      label: string;
      text: string;
      size: 'small' | 'medium';
    } | null;
    coreToInternet: {
      style: string;
      icon: IconProps['name'];
      label: string;
      text: string;
      size: 'small' | 'medium';
    } | null;
  };
}
const ConnectivityStatusPanel: FC<ConnectivityStatusPanelProps> = ({
  hologramSimId,
  // Someday, a new endpoint that returns connectivity status info
  // Until then, some manual props
  networkStatus,
  data,
  dataThisBillingPeriod,
  zeroByteSessions,
}) => {
  const {
    simDetails,
    isLoading: simDetailsIsLoading,
    error: simDetailsError,
  } = useSimDetails(hologramSimId);

  // TODO: Remove when we have real connected status
  const {
    deviceDetails,
    isLoading: deviceDetailsIsLoading,
    error: deviceDetailsError,
  } = useSimDeviceDetails(hologramSimId);

  const { isSuperAdmin } = useUserInfo();

  const {
    error: openSessionsError,
    isLoading: openSessionsLoading,
    deviceIdsWithOpenSessions,
  } = useOpenSessions(simDetails.organizationId, isSuperAdmin || !!simDetails.organizationId);

  const { networkStatusGroups } = useSimStatuses();
  const realNetworkState = useMemo(() => {
    if (
      deviceDetails.claimedDeviceId &&
      deviceIdsWithOpenSessions.has(deviceDetails.claimedDeviceId) &&
      !networkStatusGroups?.deactivated?.includes(simDetails.networkStatus as any)
    ) {
      if (networkStatusGroups?.predeployment?.includes(simDetails.networkStatus as any)) {
        return 'TEST-CONNECTED';
      }
      return 'CONNECTED';
    }
    return simDetails.networkStatus;
  }, [
    deviceDetails.claimedDeviceId,
    deviceIdsWithOpenSessions,
    networkStatusGroups?.predeployment,
    simDetails.networkStatus,
    networkStatusGroups?.deactivated,
  ]);

  const {
    statusInfo,
    isLoading: statusMapLoading,
    error: statusMapError,
  } = useStatusMap(realNetworkState ?? networkStatus);

  const diagram = useMemo((): ConnectivityDiagramProps => {
    // Define a default state (basicaly, all disabled)
    const diagramState: ConnectivityDiagramProps = {
      bars: {
        bar1: styles.disabled,
        bar2: styles.disabled,
        bar3: styles.disabled,
      },
      entities: {
        device: {
          style: styles.disabled,
          icon: 'Chip',
        },
        sim: {
          style: styles.disabled,
          icon: 'SIM',
        },
        core: {
          style: styles.disabled,
          icon: 'Compass',
        },
        internet: {
          style: styles.disabled,
          icon: 'Network',
        },
      },
      statuses: {
        deviceToSim: null,
        simToCore: null,
        coreToInternet: null,
      },
    };

    const networkState = realNetworkState ?? networkStatus;
    if (!networkState) {
      return diagramState;
    }

    if (['TEST-AUTOACTIVATE', 'TEST-ACTIVATE', 'TEST-CONNECTED'].includes(networkState)) {
      diagramState.bars.bar1 = styles.predeployment;
      diagramState.bars.bar2 = styles.predeployment;
      diagramState.entities.device.style = styles.predeployment;
      diagramState.entities.sim.style = styles.predeployment;
      diagramState.entities.core.style = styles.predeployment;

      if (data || networkState === 'TEST-CONNECTED') {
        diagramState.bars.bar3 = styles.predeployment;
        diagramState.entities.internet.style = styles.predeployment;
      }

      if (networkState === 'TEST-CONNECTED') {
        diagramState.statuses.coreToInternet = {
          style: styles.predeployment,
          icon: 'StatusConnected',
          label: 'Connected',
          text: '',
          size: 'small',
        };
      } else {
        diagramState.statuses.simToCore = {
          style: styles.predeployment,
          icon: 'StatusTesting',
          label: 'Ready',
          text: networkState,
          size: 'small',
        };
      }
    }

    if (networkState === 'LIVE-PENDING') {
      diagramState.bars.bar1 = styles.pending;
      diagramState.bars.bar2 = styles.pending;
      diagramState.entities.device.style = styles.pending;
      diagramState.entities.sim.style = styles.pending;
      diagramState.entities.core.style = styles.pending;

      // If the SIM has used data
      if (data || dataThisBillingPeriod) {
        diagramState.entities.device.style = styles.live;
        diagramState.entities.sim.style = styles.live;
        diagramState.bars.bar3 = styles.pending;
        diagramState.entities.internet.style = styles.pending;
        diagramState.statuses.coreToInternet = {
          style: styles.pending,
          icon: 'Configuring',
          label: 'Configuring',
          text: networkState,
          size: 'medium',
        };
      }
      // If the SIM has not used data
      else {
        diagramState.statuses.simToCore = {
          style: styles.pending,
          icon: 'StatusActivating',
          label: 'Pending', // TODO: Change to "Activating" when we have complete API info
          text: networkState,
          size: 'medium',
        };
      }
    }

    if (networkState === 'LIVE') {
      diagramState.bars.bar1 = styles.live;
      diagramState.bars.bar2 = styles.live; // remove this when we have ability to query for data this billing period
      diagramState.entities.device.style = styles.live;
      diagramState.entities.sim.style = styles.live;
      diagramState.entities.core.style = styles.live; // remove this when we have ability to query for data this billing period

      diagramState.statuses.simToCore = {
        style: styles.live,
        icon: 'StatusReady',
        label: 'Ready',
        text: networkState,
        size: 'small',
      };

      if (data || dataThisBillingPeriod) {
        diagramState.bars.bar2 = styles.live;
        diagramState.entities.core.style = styles.live;
      }

      if (dataThisBillingPeriod) {
        diagramState.bars.bar3 = styles.live;
        diagramState.entities.internet.style = styles.live;
      }

      if (zeroByteSessions) {
        diagramState.bars.bar2 = styles.pending;
        diagramState.bars.bar3 = styles.pending;
        diagramState.entities.core.style = styles.pending;
        diagramState.entities.internet.style = styles.pending;

        diagramState.statuses.simToCore = {
          style: styles.pending,
          icon: 'ZeroData',
          label: 'Zero bytes',
          text: networkState,
          size: 'medium',
        };
      }
    }

    if (networkState === 'CONNECTED') {
      diagramState.bars.bar1 = styles.connected;
      diagramState.bars.bar2 = styles.connected;
      diagramState.bars.bar3 = styles.connected;
      diagramState.entities.device.style = styles.connected;
      diagramState.entities.sim.style = styles.connected;
      diagramState.entities.core.style = styles.connected;
      diagramState.entities.internet.style = styles.connected;
      diagramState.statuses.coreToInternet = {
        style: styles.connected,
        icon: 'StatusConnected',
        label: 'Connected',
        text: networkState,
        size: 'small',
      };
    }

    if (['PAUSED-PENDING-SYS', 'PAUSED-PENDING-USER'].includes(networkState)) {
      diagramState.bars.bar1 = styles.live;
      diagramState.bars.bar2 = styles.paused;
      diagramState.entities.device.style = styles.live;
      diagramState.entities.sim.style = styles.paused;
      diagramState.entities.core.style = styles.paused;
      diagramState.statuses.simToCore = {
        style: styles.paused,
        icon: 'Autopause',
        label: 'Pausing',
        text: networkState,
        size: 'medium',
      };
    }

    if (['PAUSED-SYS', 'PAUSED-USER'].includes(networkState)) {
      diagramState.bars.bar1 = styles.paused;
      diagramState.bars.bar2 = styles.paused;
      diagramState.entities.device.style = styles.paused;
      diagramState.entities.sim.style = styles.paused;
      diagramState.entities.core.style = styles.paused;
      diagramState.statuses.coreToInternet = {
        style: styles.paused,
        icon: 'Pause',
        label: 'Paused',
        text: networkState,
        size: 'small',
      };
    }

    if (networkState === 'DEAD-PENDING') {
      diagramState.bars.bar1 = styles.live;
      diagramState.bars.bar2 = styles.deactivated;
      diagramState.entities.device.style = styles.live;
      diagramState.entities.sim.style = styles.deactivated;
      diagramState.entities.core.style = styles.deactivated;
      diagramState.statuses.simToCore = {
        style: styles.deactivated,
        icon: 'AutoRefill',
        label: 'Deactivating',
        text: networkState,
        size: 'medium',
      };
    }

    if (networkState === 'DEAD') {
      diagramState.entities.sim.style = styles.deactivated;
      diagramState.statuses.deviceToSim = {
        style: styles.deactivated,
        icon: 'StatusDeactivated',
        label: 'Deactivated',
        text: networkState,
        size: 'medium',
      };
      diagramState.statuses.simToCore = {
        style: styles.deactivated,
        icon: 'StatusDeactivated',
        label: 'Deactivated',
        text: networkState,
        size: 'medium',
      };
    }

    return diagramState;
  }, [realNetworkState, networkStatus, data, dataThisBillingPeriod, zeroByteSessions]);

  const error = simDetailsError || deviceDetailsError || openSessionsError || statusMapError;

  const isLoading =
    simDetailsIsLoading || deviceDetailsIsLoading || openSessionsLoading || statusMapLoading;

  let content: React.ReactNode;
  if (isLoading) {
    content = <Loader />;
  } else if (error) {
    content = <PanelNotification>Error loading connectivity status</PanelNotification>;
  } else {
    content = (
      <>
        <div className={styles.chipAndStatus}>
          <div className={styles.chip}>
            <SimStatusChip
              networkState={realNetworkState ?? networkStatus}
              size="medium"
              popoverContent={
                <div>
                  <span className={styles.popoverStatusLabel}>Network state</span>
                  <br />
                  {realNetworkState ?? networkStatus}
                </div>
              }
            />
          </div>
          <div className={styles.chipStatus}>{statusInfo?.tooltip}</div>
        </div>
        {zeroByteSessions && (realNetworkState ?? networkStatus) === 'LIVE' && (
          <div className={styles.bannerNotification}>
            <BannerNotification variant="caution" prefixOverride="Warning">
              This SIM has closed sessions but has not passed any data in this billing period. This
              could indicate an APN configuration issue with your device.
            </BannerNotification>
          </div>
        )}
        <div className={styles.diagram}>
          <div className={styles.bars}>
            <div className={clsx(styles.bar, diagram.bars.bar1)} />
            <div className={clsx(styles.bar, diagram.bars.bar2)} />
            <div className={clsx(styles.bar, diagram.bars.bar3)} />
          </div>
          <div className={styles.entities}>
            <div className={clsx(styles.entity, diagram.entities.device.style)}>
              <div className={styles.entityIcon}>
                <Icon name={diagram.entities.device.icon} size="medium" fill="currentColor" />
              </div>
              <div className={styles.entityLabel}>
                <span className={styles.entityName}>Your device</span>
                <span className={styles.entityDescription}>
                  <ClickToCopy
                    value={deviceDetails.claimedDeviceId?.toString() ?? ''}
                    label={
                      <>
                        <span>
                          <strong>Device ID</strong>
                        </span>
                        <span>{deviceDetails.claimedDeviceId}</span>
                      </>
                    }
                  />
                </span>
              </div>
            </div>
            <div className={clsx(styles.entity, diagram.entities.sim.style)}>
              <div className={styles.entityIcon}>
                <Icon name={diagram.entities.sim.icon} size="medium" fill="currentColor" />
              </div>
              <div className={styles.entityLabel}>
                <span className={styles.entityName}>Hologram SIM</span>
                <span className={styles.entityDescription}>
                  <ClickToCopy
                    value={hologramSimId?.toString() ?? ''}
                    label={
                      <>
                        <span>
                          <strong>SIM ID</strong>
                        </span>
                        <span>{hologramSimId}</span>
                      </>
                    }
                  />
                </span>
              </div>
            </div>
            <div className={clsx(styles.entity, diagram.entities.core.style)}>
              <div className={styles.entityIcon}>
                <Icon name={diagram.entities.core.icon} size="medium" fill="currentColor" />
              </div>
              <div className={styles.entityLabel}>
                <span className={styles.entityName}>Mobile core operator</span>
                <span className={styles.entityDescription}>
                  <ClickToCopy
                    value={simDetails.activeProfile?.toString() ?? ''}
                    label={
                      <>
                        <span>
                          <strong>{simDetails.profileName}</strong>
                        </span>
                        <span className={styles.hidden}>Profile ID {simDetails.activeProfile}</span>
                      </>
                    }
                  />
                </span>
              </div>
            </div>
            <div className={clsx(styles.entity, diagram.entities.internet.style)}>
              <div className={styles.entityIcon}>
                <Icon name={diagram.entities.internet.icon} size="medium" fill="currentColor" />
              </div>
              <div className={styles.entityLabel}>
                <span className={styles.entityName}>Internet</span>
              </div>
            </div>
          </div>
          <div className={styles.statuses}>
            <div
              className={clsx(
                styles.status,
                diagram.statuses.deviceToSim?.style,
                !diagram.statuses.deviceToSim && styles.hidden
              )}
            >
              <div className={styles.statusIcon}>
                <Icon
                  name={diagram.statuses.deviceToSim?.icon}
                  size={diagram.statuses.deviceToSim?.size}
                />
              </div>
              <div className={styles.statusLabel}>
                <div className={styles.statusName}>{diagram.statuses.deviceToSim?.label}</div>
                <div className={clsx(styles.statusDescription, styles.hidden)}>
                  {diagram.statuses.deviceToSim?.text}
                </div>
              </div>
            </div>
            <div
              className={clsx(
                styles.status,
                diagram.statuses.simToCore?.style,
                !diagram.statuses.simToCore && styles.hidden
              )}
            >
              <div className={styles.statusIcon}>
                <Icon
                  name={diagram.statuses.simToCore?.icon}
                  size={diagram.statuses.simToCore?.size}
                />
              </div>
              <div className={styles.statusLabel}>
                <div className={styles.statusName}>{diagram.statuses.simToCore?.label}</div>
                <div className={clsx(styles.statusDescription, styles.hidden)}>
                  {diagram.statuses.simToCore?.text}
                </div>
              </div>
            </div>
            <div
              className={clsx(
                styles.status,
                diagram.statuses.coreToInternet?.style,
                !diagram.statuses.coreToInternet && styles.hidden
              )}
            >
              <div className={styles.statusIcon}>
                <Icon
                  name={diagram.statuses.coreToInternet?.icon}
                  size={diagram.statuses.coreToInternet?.size}
                />
              </div>
              <div className={styles.statusLabel}>
                <div className={styles.statusName}>{diagram.statuses.coreToInternet?.label}</div>
                <div className={clsx(styles.statusDescription, styles.hidden)}>
                  {diagram.statuses.coreToInternet?.text}
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <Panel header="Status" noContentPadding>
      {content}
    </Panel>
  );
};

export default ConnectivityStatusPanel;
